import React from "react";
import { styled } from "@mui/system";
import CrossIcon from "src/icons/CrossIcon";
import ProfileInitials from "./ProfileInitials";
import { useMediaQuery, useTheme } from "@mui/material";

const Wrapper = styled("div")(({ theme }) => ({
  display: "flex",
  width: "100%",
  alignItems: "flex-start",
  gap: "12px",
  background: "#FFF",

  [theme.breakpoints.down(900)]: {
    background: "#002035",
    width: "100%",
    padding: "24px",
  },
}));

const MedalWrapper = styled("div")(() => ({
  display: "flex",
  width: "64px",
  height: "64px",
  padding: "28.444px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "7.111px",
  flexShrink: 0,
  borderRadius: "177.778px",
}));

const TileWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "8px",
  flex: "1 0 0",
}));

const Text = styled("div")(({ theme }) => ({
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "18px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "22px",

  [theme.breakpoints.down(900)]: {
    color: "#FFF",
  },
}));

const SubText = styled("div")(({ theme }) => ({
  color: "#002035",
  fontFamily: "Roobert",
  fontSsize: "16px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "19px",

  [theme.breakpoints.down(900)]: {
    color: "#FFF",
  },
}));

const CloseIconWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  width: "12px",
  height: "12px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  cursor: "pointer",

  [theme.breakpoints.down(900)]: {
    height: "100%",
  },
}));

interface ProfileHeaderModalProps {
  name: string;
  customerID: string;
  tier: "bronze" | "silver" | "gold" | "black";
  accountLocked: boolean;
  showNotification?: boolean;
  onClick?: () => void;
}

/**
 * A header modal component for displaying profile information.
 * @param {string} props.name - The name of the profile.
 * @param {string} props.customerID - The customer ID of the profile.
 * @param {"bronze" | "silver" | "gold" | "black"} props.tier - The tier of the profile (bronze, silver, gold, or black).
 * @param {boolean} props.showNotification - Boolean to determine whether to show nofication circle or not (optional).
 *  * @param {function} [props.onClick] - The function to run when user clicks on component (optional).
 */
const ProfileHeaderModal = (props: ProfileHeaderModalProps) => {
  const { name, customerID, tier, accountLocked, showNotification = false, onClick } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(900));
  const iconColor = isMobile ? "#FFF" : "#002035";

  const initials = React.useMemo((): string => {
    // Split the name into an array of words
    const words = name.split(" ");

    // Initialize an empty string to store initials
    let initials = "";

    // Iterate over each word in the array
    for (const word of words) {
      // Get the first character of each word and convert it to uppercase
      initials += word.charAt(0).toUpperCase();
    }

    // Return the initials
    return initials.slice(0, 2);
  }, [name]);

  return (
    <Wrapper>
      <MedalWrapper>
        <ProfileInitials
          initials={initials}
          tier={tier}
          notification={showNotification}
          accountLocked={accountLocked}
          scale="1.8"
          filled
        />
      </MedalWrapper>
      <TileWrapper>
        <Text>{name}</Text>
        <SubText>{customerID}</SubText>
      </TileWrapper>
      <CloseIconWrapper onClick={onClick}>
        <CrossIcon color={iconColor} width="12" height="12" />
      </CloseIconWrapper>
    </Wrapper>
  );
};

export default ProfileHeaderModal;
